import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HomeFooter } from '../components/home/HomeFooter';
import { HomeHeader } from '../components/home/HomeHeader';
import { ChevronIcon } from '../components/shared/Icons';
import { HEADER_SEARCH_KEYWORD_SUGGESTIONS } from '../config/searchSuggestions';
import { loadAuthSession } from '../data/authSession';

const RETAINED_DATA = [
  { label: '계약 또는 청약철회 등에 관한 기록', period: '5년', law: '전자상거래 등에서의 소비자보호에 관한 법률' },
  { label: '대금결제 및 재화 등의 공급에 관한 기록', period: '5년', law: '전자상거래 등에서의 소비자보호에 관한 법률' },
  { label: '소비자의 불만 또는 분쟁처리에 관한 기록', period: '3년', law: '전자상거래 등에서의 소비자보호에 관한 법률' },
  { label: '전자금융 거래에 관한 기록', period: '5년', law: '전자금융거래법' },
  { label: '접속 로그 등 서비스 이용 기록', period: '3개월', law: '통신비밀보호법' },
];

export function AccountDeletionGuidePage() {
  const navigate = useNavigate();
  const isAuthenticated = Boolean(loadAuthSession());
  const [headerSearchOpen, setHeaderSearchOpen] = useState(false);
  const [headerSearchQuery, setHeaderSearchQuery] = useState('');
  const headerSearchRef = useRef<HTMLDivElement | null>(null);

  const filteredSuggestions = useMemo(
    () =>
      HEADER_SEARCH_KEYWORD_SUGGESTIONS.filter((item) =>
        item.toLowerCase().includes(headerSearchQuery.toLowerCase()),
      ),
    [headerSearchQuery],
  );

  const onHeaderSearchSubmit = useCallback(
    (value: string) => {
      const q = value.trim();
      if (!q) return;
      navigate(`/search?q=${encodeURIComponent(q)}`);
    },
    [navigate],
  );

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!headerSearchRef.current?.contains(event.target as Node)) {
        setHeaderSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  return (
    <main className="terms-page">
      <HomeHeader
        authenticated={isAuthenticated}
        filteredSuggestions={filteredSuggestions}
        onGuestCta={() => navigate('/login')}
        onSearchChange={(value) => {
          setHeaderSearchQuery(value);
          setHeaderSearchOpen(Boolean(value.trim()));
        }}
        onSearchClear={() => {
          setHeaderSearchQuery('');
          setHeaderSearchOpen(false);
        }}
        onSearchFocus={() => setHeaderSearchOpen(Boolean(headerSearchQuery.trim()))}
        onSearchSubmit={onHeaderSearchSubmit}
        onSuggestionSelect={(value) => {
          setHeaderSearchOpen(false);
          onHeaderSearchSubmit(value);
        }}
        searchOpen={headerSearchOpen}
        searchQuery={headerSearchQuery}
        searchRef={headerSearchRef}
      />

      <div className="terms-page__main">
        <section className="terms">
          <div className="terms__head">
            <button
              aria-label="뒤로"
              className="terms__back"
              onClick={() => navigate(-1)}
              type="button"
            >
              <span aria-hidden className="terms__back-chevron">
                <ChevronIcon />
              </span>
            </button>
            <h1 className="terms__title">계정 삭제 안내</h1>
          </div>

          <div className="terms__articles">
            <article className="terms__article">
              <h2 className="terms__article-heading">서비스 및 운영자</h2>
              <div className="terms__article-body">
                <p>
                  본 안내는 밴더(BANDER) 모바일 앱 및 웹 서비스의 계정(회원) 삭제 절차를 설명합니다.
                </p>
                <ul>
                  <li>서비스명: 밴더(BANDER)</li>
                  <li>운영사: 주식회사 팀바인드</li>
                  <li>문의: policy@teambind.co.kr</li>
                </ul>
              </div>
            </article>

            <article className="terms__article">
              <h2 className="terms__article-heading">계정 삭제 요청 방법</h2>
              <div className="terms__article-body">
                <p>아래 방법 중 하나로 직접 계정을 삭제(회원 탈퇴)할 수 있습니다.</p>
                <ol>
                  <li>
                    <strong>앱에서</strong>: 마이페이지 → 계정 설정 → 화면 하단의 ‘회원탈퇴’를 선택합니다.
                  </li>
                  <li>
                    <strong>웹에서</strong>: 로그인 후{' '}
                    <Link to="/account/settings">계정 설정</Link> 페이지 하단의 ‘회원탈퇴’를 선택합니다.
                  </li>
                  <li>
                    <strong>이메일로</strong>: 위 방법을 이용하기 어려운 경우, 가입 시 사용한 이메일 주소와 함께{' '}
                    <a href="mailto:policy@teambind.co.kr">policy@teambind.co.kr</a> 으로 삭제를 요청해 주세요.
                  </li>
                </ol>
              </div>
            </article>

            <article className="terms__article">
              <h2 className="terms__article-heading">삭제되는 데이터</h2>
              <div className="terms__article-body">
                <p>계정 삭제 시 다음 정보가 삭제 처리됩니다.</p>
                <ul>
                  <li>계정 정보: 이메일 주소, 비밀번호(복호화 불가능한 일방향 암호화로 저장)</li>
                  <li>프로필 정보: 닉네임, 성별, 활동 지역, 프로필 이미지, 자기소개, 음악 장르·악기</li>
                  <li>휴대전화번호 및 본인확인 정보</li>
                  <li>작성한 리뷰·게시글·댓글 등 서비스 활동 내역</li>
                </ul>
                <p>
                  탈퇴 요청 시 계정은 즉시 비활성화되어 다시 로그인할 수 없으며, 아래 ‘보관되는 데이터’를
                  제외한 개인정보는 관계 법령에 따라 지체 없이 파기됩니다.
                </p>
              </div>
            </article>

            <article className="terms__article">
              <h2 className="terms__article-heading">보관되는 데이터 및 보관 기간</h2>
              <div className="terms__article-body">
                <p>
                  관계 법령에 따라 일정 기간 보관이 의무화된 아래 정보는 탈퇴 후에도 해당 기간 동안
                  분리 보관된 뒤 파기됩니다.
                </p>
                <ul>
                  {RETAINED_DATA.map((item) => (
                    <li key={item.label}>
                      {item.label}: <strong>{item.period}</strong> ({item.law})
                    </li>
                  ))}
                </ul>
              </div>
            </article>

            <article className="terms__article">
              <h2 className="terms__article-heading">처리 절차 및 문의</h2>
              <div className="terms__article-body">
                <p>
                  계정 삭제 요청은 접수 즉시 처리되며, 법령상 보관 의무가 없는 개인정보는 지체 없이
                  파기됩니다. 계정 삭제와 관련한 문의는 아래로 연락해 주세요.
                </p>
                <ul>
                  <li>주식회사 팀바인드</li>
                  <li>이메일: policy@teambind.co.kr</li>
                  <li>주소: 인천광역시 연수구 인천타워대로54번길 15-3, 501·502호</li>
                </ul>
              </div>
            </article>
          </div>
        </section>
      </div>

      <HomeFooter />
    </main>
  );
}
